import SEOHead from '@/components/base/SEOHead';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/categories';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://hipobuy.study';
const PAGE_PATH = '/guide-achat';
const CANONICAL = `${SITE_URL}${PAGE_PATH}`;

const seoData = {
  title: 'Guide d\'Achat Hipobuy Spreadsheet – Comment Acheter sur Kaspreadsheet',
  description: 'Guide complet pour naviguer dans les 14 catégories de produits Hipobuy sur Kaspreadsheet. Shoes, Hoodies, T-Shirts, Jackets et plus. Achetez facilement en quelques clics.',
  keywords: 'guide achat hipobuy spreadsheet, comment acheter kaspreadsheet, catégories produits hipobuy, catalogue mode, shopping kaspreadsheet',
};

const webpageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: seoData.title,
  description: seoData.description,
  url: CANONICAL,
  inLanguage: 'fr',
  isPartOf: { '@type': 'WebSite', name: 'Hipobuy Spreadsheet', url: SITE_URL },
};

export default function GuideAchat() {
  return (
    <main>
      <SEOHead
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonicalUrl={CANONICAL}
        ogType="website"
        schemaJsonLd={webpageSchema}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative w-full pt-[72px] bg-gradient-to-b from-background-200 to-background-50 py-16 md:py-24">
        <div className="w-full max-w-[900px] mx-auto px-6 md:px-10 text-center">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors mb-6 group">
            <span className="w-4 h-4 flex items-center justify-center group-hover:-translate-x-1 transition-transform">
              <i className="ri-arrow-left-line"></i>
            </span>
            Retour à l&apos;accueil
          </Link>
          <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950 mb-5">
            Guide d&apos;Achat Hipobuy Spreadsheet
          </h1>
          <p className="text-base text-foreground-500 max-w-[600px] mx-auto">
            Comment naviguer dans notre catalogue de 14 catégories produits et profiter pleinement de Hipobuy Spreadsheet sur Kaspreadsheet.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="relative w-full bg-background-50 py-12 md:py-20">
        <div className="w-full max-w-[780px] mx-auto px-6 md:px-10">
          <h2 className="text-2xl font-heading font-semibold text-foreground-950 mb-6">Comment Acheter sur Kaspreadsheet</h2>
          <p className="text-base text-foreground-600 leading-relaxed mb-8">
            <strong>Hipobuy Spreadsheet</strong> est accessible via la plateforme Kaspreadsheet. Suivez ce guide simple pour explorer notre catalogue de produits répartis en 14 catégories distinctes, des chaussures aux accessoires, en passant par les vêtements et les parfums.
          </p>

          <h2 className="text-2xl font-heading font-semibold text-foreground-950 mb-6">Toutes les Catégories</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
            {categories.map((cat) => (
              <a key={cat.id} href={cat.url} target="_blank" rel="noopener nofollow" className="flex items-center gap-4 p-4 rounded-xl bg-background-100/80 border border-background-200/70 hover:border-primary-300/70 transition-all cursor-pointer group">
                <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                  <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-foreground-800">{cat.name}</h4>
                  <p className="text-xs text-foreground-500 mt-0.5">{cat.description}</p>
                </div>
              </a>
            ))}
          </div>

          <h2 className="text-2xl font-heading font-semibold text-foreground-950 mb-4">Pourquoi Utiliser Hipobuy Spreadsheet ?</h2>
          <p className="text-base text-foreground-600 leading-relaxed mb-4">
            <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">Hipobuy Spreadsheet</Link> offre une expérience de gestion de données moderne, avec automatisation intelligente, analyse avancée et interface intuitive. Que vous gériez un petit catalogue ou des milliers de références, notre outil s&apos;adapte à vos besoins.
          </p>
          <p className="text-base text-foreground-600 leading-relaxed mb-8">
            Accédez à la plateforme via <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:text-primary-700 font-medium">kaspreadsheet.com</a> pour découvrir toutes les fonctionnalités de <strong>Hipobuy Spreadsheet</strong>.
          </p>

          <div className="flex flex-wrap gap-4">
            <Link to="/" className="btn-cta inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3 rounded-full text-sm font-semibold transition-all whitespace-nowrap">
              Retour à l&apos;accueil
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-home-4-line"></i></span>
            </Link>
            <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="btn-cta inline-flex items-center gap-2 bg-foreground-900 hover:bg-foreground-800 text-background-50 px-6 py-3 rounded-full text-sm font-semibold transition-all whitespace-nowrap">
              buy it now
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-shopping-cart-line"></i></span>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}